import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import PageContainer from '../components/layout/PageContainer';
import RichTextEditor from '../components/editor/RichTextEditor';
import Button from '../components/ui/Button';
import ImageUploader from '../components/ui/ImageUploader';
import CropModal from '../components/ui/CropModal';
import { ArrowLeft, Save, Globe, Lock, Image, Move, MapPin } from 'lucide-react';
import { getSourceConfig } from '../constants/sourceTypeConfig';
import { useAuth } from '../contexts/AuthContext';

const CONTINENTS = ['Europe', 'Asia', 'North America', 'South America', 'Africa', 'Oceania', 'Middle East'];

const escapeHtml = (str) =>
  String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// Builds one continuous editor document from the stored per-day blocks, so
// loading an existing Atlas File round-trips into the single "Your Journey"
// editor without losing day titles or (now-inline) photos.
const buildJourneyHtml = (days) =>
  (days || [])
    .map((day) => {
      const heading = `<h2>${escapeHtml(day.title || `Day ${day.dayNumber}`)}</h2>`;
      const images = (day.images || []).map((src) => `<img src="${src}" alt="" />`).join('');
      return heading + (day.content || '') + images;
    })
    .join('');

// Splits the single editor document back into the days[] shape the database
// and the public Atlas File page expect, using each H2 as a day boundary.
// Content typed before the first H2 (or with no headings at all) still
// becomes "Day 1" rather than being silently dropped.
const splitJourneyIntoDays = (html) => {
  if (!html || !html.trim()) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const blocks = [];
  let current = null;

  Array.from(doc.body.childNodes).forEach((node) => {
    const isHeading = node.nodeType === 1 && node.tagName === 'H2';
    if (isHeading) {
      current = { title: node.textContent.trim() || `Day ${blocks.length + 1}`, parts: [] };
      blocks.push(current);
      return;
    }
    if (!current) {
      current = { title: 'Day 1', parts: [] };
      blocks.push(current);
    }
    const markup = node.nodeType === 1 ? node.outerHTML : node.textContent;
    if (markup && markup.trim()) current.parts.push(markup);
  });

  return blocks.map((block, i) => ({
    dayNumber: i + 1,
    title: block.title,
    content: block.parts.join(''),
    images: [],
  }));
};

// For the live "days detected" readout — unlike splitJourneyIntoDays, this
// has no "Day 1" fallback, so it only reports markers that actually exist.
const getDayMarkerTitles = (html) => {
  if (!html || !html.trim()) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return Array.from(doc.body.querySelectorAll(':scope > h2')).map((h, i) => h.textContent.trim() || `Day ${i + 1}`);
};

function AtlasFileEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromItinerary = searchParams.get('fromItinerary');
  const typeParam = searchParams.get('type');
  const { isAdmin } = useAuth();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id || !!fromItinerary);
  const [isPublic, setIsPublic] = useState(false);
  const [repositioningCover, setRepositioningCover] = useState(false);
  const coverImgRef = useRef(null);
  const [sourceType, setSourceType] = useState(
    (!id && isAdmin && typeParam === 'curated') ? 'curated' : 'traveler'
  );
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    destination: '',
    continent: '',
    country: '',
    trip_length: 1,
    cover_image_url: '',
  });
  const [intro, setIntro] = useState('');
  const [tips, setTips] = useState('');
  const [journey, setJourney] = useState('<h2>Day 1</h2>');
  const journeyDayTitles = useMemo(() => getDayMarkerTitles(journey), [journey]);

  useEffect(() => {
    if (id) {
      fetchAtlasFile();
    } else if (fromItinerary) {
      importFromItinerary();
    }
  }, [id, fromItinerary]);

  const fetchAtlasFile = async () => {
    try {
      const { data, error } = await supabase
        .from('atlas_files')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setFormData({
        title: data.title || '',
        description: data.description || '',
        destination: data.destination || '',
        continent: data.continent || '',
        country: data.country || '',
        trip_length: data.trip_length || 1,
        cover_image_url: data.cover_image_url || '',
      });
      setIsPublic(!!data.published_at);
      setSourceType(data.source_type || 'traveler');

      if (data.content) {
        const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        setIntro(content.intro || '');
        setTips(content.tips || '');
        setJourney(buildJourneyHtml(content.days) || '<h2>Day 1</h2>');
      }
    } catch (error) {
      console.error('Error fetching atlas file:', error);
      alert('Failed to load atlas file');
      navigate('/atlas');
    } finally {
      setLoading(false);
    }
  };

  const importFromItinerary = async () => {
    try {
      const { data: itinerary, error: itErr } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', fromItinerary)
        .single();

      if (itErr) throw itErr;

      const { data: activities, error: actErr } = await supabase
        .from('activities')
        .select('*')
        .eq('itinerary_id', fromItinerary)
        .order('day_number', { ascending: true })
        .order('position', { ascending: true });

      if (actErr) throw actErr;

      setFormData({
        title: itinerary.title || '',
        description: `A ${itinerary.trip_length}-day trip to ${itinerary.destination}`,
        destination: itinerary.destination || '',
        trip_length: itinerary.trip_length || 1,
        cover_image_url: itinerary.thumbnail_url || '',
      });

      const dayGroups = {};
      (activities || []).forEach(act => {
        const d = act.day_number || 1;
        if (!dayGroups[d]) dayGroups[d] = [];
        dayGroups[d].push(act);
      });

      const importedDays = Array.from({ length: itinerary.trip_length }, (_, i) => {
        const dayNum = i + 1;
        const dayActs = dayGroups[dayNum] || [];
        const cityName = dayActs[0]?.city_name;
        const dayTitle = cityName ? `Day ${dayNum} — ${cityName}` : `Day ${dayNum}`;
        const content = dayActs.map(a => {
          let html = `<h3>${a.title}</h3>`;
          if (a.description) html += `<p>${a.description}</p>`;
          if (a.location) html += `<p><strong>Location:</strong> ${a.location}</p>`;
          if (a.duration_minutes) html += `<p><strong>Duration:</strong> ${Math.floor(a.duration_minutes / 60)}h ${a.duration_minutes % 60}m</p>`;
          return html;
        }).join('');

        return { dayNumber: dayNum, title: dayTitle, content, images: [] };
      });

      setJourney(buildJourneyHtml(importedDays) || '<h2>Day 1</h2>');
    } catch (error) {
      console.error('Error importing itinerary:', error);
      alert('Failed to import itinerary');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCoverUpload = async (url) => {
    handleFieldChange('cover_image_url', url);
    if (id) {
      await supabase.from('atlas_files').update({ cover_image_url: url }).eq('id', id);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('Please log in to save');
        navigate('/login');
        return;
      }

      const days = splitJourneyIntoDays(journey);
      const content = { intro, days, tips };

      const atlasData = {
        title: formData.title,
        description: formData.description,
        destination: formData.destination,
        continent: formData.continent || null,
        country: formData.country || null,
        trip_length: days.length || formData.trip_length,
        cover_image_url: formData.cover_image_url || null,
        content,
        author_id: user.id,
        author: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Anonymous',
        published_at: isPublic ? new Date().toISOString() : null,
      };

      // Admin curated stories are auto-approved — both as drafts and when
      // published — so editing never accidentally flips them back to
      // "pending review" and they appear on the globe immediately on publish.
      const isAdminCurated = isAdmin && sourceType === 'curated';

      if (id) {
        const updateData = { ...atlasData };
        if (isAdminCurated) updateData.moderation_status = 'approved';
        const { error } = await supabase
          .from('atlas_files')
          .update(updateData)
          .eq('id', id);
        if (error) throw error;
      } else {
        const insertData = { ...atlasData, source_type: sourceType };
        if (isAdminCurated) insertData.moderation_status = 'approved';
        const { data, error } = await supabase
          .from('atlas_files')
          .insert(insertData)
          .select()
          .single();
        if (error) throw error;
        navigate(`/atlas/edit/${data.id}`, { replace: true });
      }

      const saveMessage = !isPublic
        ? 'Draft saved!'
        : (isAdmin && sourceType === 'curated')
          ? 'Published! Your curated story is now live.'
          : 'Submitted for review! Your content will be visible once approved by an admin.';
      alert(saveMessage);
    } catch (error) {
      console.error('Error saving atlas file:', error);
      alert('Failed to save: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400"></div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/designer')}
            className="flex items-center gap-2 text-charcoal-400 hover:text-charcoal-500 transition-colors"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="flex items-center gap-3">
            {(() => {
              const config = getSourceConfig(sourceType);
              const TypeIcon = config.icon;
              return (
                <span className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full font-medium ${config.badge.bg} ${config.badge.text}`}>
                  <TypeIcon size={12} />
                  {config.label}
                </span>
              );
            })()}
            <button
              type="button"
              onClick={() => setIsPublic(!isPublic)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isPublic
                  ? 'bg-coral-100 text-coral-700'
                  : 'bg-platinum-100 text-platinum-600'
              }`}
            >
              {isPublic ? <Globe size={14} /> : <Lock size={14} />}
              {isPublic ? 'Public' : 'Private'}
            </button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-2">
              <Save size={16} />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Cover Image Area */}
        <div className="mb-8">
          {formData.cover_image_url ? (
            <div className="relative rounded-2xl overflow-hidden">
              <img ref={coverImgRef} src={formData.cover_image_url} alt="Cover" className="w-full h-56 object-cover" />
              <div className="absolute inset-0 bg-charcoal-500/30 flex items-center justify-center gap-2 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => setRepositioningCover(true)}
                  className="bg-white rounded-xl px-4 py-2 text-sm font-medium text-charcoal-500 flex items-center gap-2 shadow-lg"
                >
                  <Move size={16} /> Reposition
                </button>
                <button
                  type="button"
                  onClick={() => handleFieldChange('cover_image_url', '')}
                  className="bg-white rounded-xl px-4 py-2 text-sm font-medium text-charcoal-500 flex items-center gap-2 shadow-lg"
                >
                  <Image size={16} /> Change Cover
                </button>
              </div>
            </div>
          ) : (
            <ImageUploader
              onUpload={handleCoverUpload}
              crop={false}
            />
          )}
        </div>

        {repositioningCover && (
          <CropModal
            src={formData.cover_image_url}
            initialRatio={coverImgRef.current ? coverImgRef.current.offsetWidth / coverImgRef.current.offsetHeight : 16 / 9}
            onCancel={() => setRepositioningCover(false)}
            onConfirm={(url) => {
              handleCoverUpload(url);
              setRepositioningCover(false);
            }}
          />
        )}

        {/* Title & Meta — Journal Style */}
        <div className="mb-10">
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            placeholder="Give your atlas file a title..."
            className="w-full text-3xl md:text-4xl font-semibold text-charcoal-500 bg-transparent border-none focus:outline-none placeholder:text-platinum-400 mb-3"
          />
          <textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="A short summary of your adventure..."
            className="w-full text-lg text-charcoal-400 bg-transparent border-none focus:outline-none placeholder:text-platinum-400 resize-none h-16"
          />

          <div className="flex flex-wrap gap-3 mt-4">
            <div className="flex items-center gap-2 bg-naples-100 rounded-xl px-4 py-2.5">
              <span className="text-sm text-charcoal-400">Destination</span>
              <input
                type="text"
                value={formData.destination}
                onChange={(e) => handleFieldChange('destination', e.target.value)}
                placeholder="e.g., Tokyo"
                className="bg-transparent font-medium text-charcoal-500 focus:outline-none text-sm w-32"
              />
            </div>
            <div className="flex items-center gap-2 bg-coral-100 rounded-xl px-4 py-2.5">
              <span className="text-sm text-charcoal-400">Days</span>
              <input
                type="number"
                min="1"
                max="60"
                value={formData.trip_length}
                onChange={(e) => handleFieldChange('trip_length', parseInt(e.target.value) || 1)}
                className="bg-transparent font-medium text-charcoal-500 focus:outline-none text-sm w-12"
              />
            </div>
            <div className="flex items-center gap-2 bg-columbia-100 rounded-xl px-4 py-2.5">
              <MapPin size={14} className="text-charcoal-400 flex-shrink-0" />
              <span className="text-sm text-charcoal-400">Continent</span>
              <select
                value={formData.continent}
                onChange={(e) => handleFieldChange('continent', e.target.value)}
                className="bg-transparent font-medium text-charcoal-500 focus:outline-none text-sm cursor-pointer"
              >
                <option value="">— select —</option>
                {CONTINENTS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 bg-platinum-100 rounded-xl px-4 py-2.5">
              <span className="text-sm text-charcoal-400">Country</span>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleFieldChange('country', e.target.value)}
                placeholder="e.g., Japan"
                className="bg-transparent font-medium text-charcoal-500 focus:outline-none text-sm w-28"
              />
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="mb-10 bg-white rounded-2xl border border-platinum-200 p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-charcoal-500 mb-3 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-naples-400 flex items-center justify-center text-charcoal-500 text-sm" style={{ fontFamily: "'Fredoka', sans-serif" }}>i</span>
            Introduction
          </h2>
          <RichTextEditor
            content={intro}
            onChange={setIntro}
            placeholder="Write an introduction to your trip..."
          />
        </div>

        {/* Your Journey — one continuous document, split into days on save */}
        <div className="mb-10 bg-white rounded-2xl border border-platinum-200 p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-charcoal-500 mb-1">Your Journey</h2>
          <p className="text-sm text-charcoal-400 mb-3">
            Paste or write the whole trip here. Use the "Day marker" button to drop in a Day heading —
            it's what gets split into day-by-day sections automatically when you save. Drag a photo in from
            your computer or paste one right where it belongs — or click an inserted photo to drag it
            elsewhere or resize it.
          </p>
          <RichTextEditor
            content={journey}
            onChange={setJourney}
            placeholder='Day 1: Palermo — start your Sicilian adventure...'
            showDayMarker
          />
          <p className="text-xs text-platinum-500 mt-2">
            {journeyDayTitles.length > 0
              ? `${journeyDayTitles.length} day${journeyDayTitles.length === 1 ? '' : 's'} detected: ${journeyDayTitles.join(', ')}`
              : 'No day markers yet — add one with the "Day marker" button above.'}
          </p>
        </div>

        {/* Tips Section */}
        <div className="mb-10 bg-white rounded-2xl border border-platinum-200 p-6 shadow-sm">
          <h2 className="text-xl font-semibold text-charcoal-500 mb-3 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-columbia-600 flex items-center justify-center text-white text-sm">&#9733;</span>
            Travel Tips
          </h2>
          <RichTextEditor
            content={tips}
            onChange={setTips}
            placeholder="Share your tips, recommendations, and things to know..."
          />
        </div>

        {/* Bottom Actions */}
        <div className="flex justify-center gap-3 pb-12 pt-4">
          <button
            type="button"
            onClick={() => setIsPublic(!isPublic)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isPublic
                ? 'bg-coral-100 text-coral-700'
                : 'bg-platinum-100 text-platinum-600'
            }`}
          >
            {isPublic ? <Globe size={16} /> : <Lock size={16} />}
            {isPublic ? 'Public' : 'Private'}
          </button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}

export default AtlasFileEditorPage;
